/**
 * «Usuarios»: autogestión de los miembros de la propia organización (ADR-007).
 *
 * Hasta aquí, dar de alta a alguien en una organización solo podía hacerlo un operador Continnua
 * desde la consola de plataforma. Esta pantalla lo pone en manos de quien administra la empresa,
 * contra las **mismas rutas** que usa esa consola (`/orgs/:orgId/members`): el núcleo distingue por
 * quién llama, no por desde dónde, así que no hay un segundo camino que mantener ni una segunda
 * regla que se pueda desincronizar.
 *
 * Dos cosas gobiernan lo que se ve y lo que se puede hacer, y ambas las decide el núcleo:
 *
 * - `canManageMembers` de `GET /me/orgs` filtra las organizaciones administrables. Es el mismo
 *   predicado que autoriza la escritura, así que esta pantalla no ofrece nada que vaya a acabar
 *   en un 403.
 * - `GET /orgs/:orgId/assignable-roles` dice qué roles se pueden repartir. No se deduce aquí
 *   cruzando módulos con suscripciones: esa regla vive en el núcleo, que es quien la valida.
 *
 * El token del núcleo está emitido para **una** organización, y `assertUserOrgAdmin` exige que sea
 * la misma que se administra. Por eso elegir otra organización cambia primero la sesión
 * (`switchOrg`, `POST /oauth/switch-org`) y solo después carga: sin ese paso, administrar la
 * segunda organización de alguien devolvería 403 sin explicar por qué.
 */

import { useAuth, useAuthorizedFetch } from "@continnua/auth-web/react";
import type { AssignableModuleRoles, MeOrgsEntry, Membership } from "@continnua/contracts";
import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from "react";

import { api } from "../api";

const ETIQUETA_ESTADO: Record<string, string> = {
  active: "Activo",
  invited: "Invitado",
  suspended: "Suspendido",
  removed: "Retirado",
};

function mensajeDe(err: unknown, porDefecto: string): string {
  return err instanceof Error && err.message.length > 0 ? err.message : porDefecto;
}

/** `{ modulo: [rol] }` sin las entradas vacías, que es lo que aceptan los DTO de invitación. */
function limpiarRoles(roles: Record<string, string[]>): Record<string, string[]> {
  return Object.fromEntries(Object.entries(roles).filter(([, codigos]) => codigos.length > 0));
}

function alternarRol(
  roles: Record<string, string[]>,
  moduleCode: string,
  roleCode: string,
  marcado: boolean,
): Record<string, string[]> {
  const actuales = roles[moduleCode] ?? [];
  const siguientes = marcado ? [...actuales, roleCode] : actuales.filter((codigo) => codigo !== roleCode);
  return { ...roles, [moduleCode]: siguientes };
}

/**
 * Casillas de rol agrupadas por módulo. El rol de administrador se anota: es el único que cambia
 * lo que la persona puede hacer **fuera** de su módulo —gestionar esta misma pantalla—, y quien
 * reparte roles merece saberlo antes de marcarlo, no después.
 */
function RolesPorModulo({
  asignables,
  seleccion,
  onAlternar,
  idPrefijo,
}: {
  asignables: readonly AssignableModuleRoles[];
  seleccion: Record<string, string[]>;
  onAlternar: (moduleCode: string, roleCode: string, marcado: boolean) => void;
  idPrefijo: string;
}): ReactElement {
  return (
    <fieldset>
      <legend>Roles</legend>
      {asignables.map((modulo) => (
        <div key={modulo.moduleCode} style={{ marginBottom: 12 }}>
          <div className="muted" style={{ marginBottom: 4 }}>
            {modulo.moduleName}
            {!modulo.subscribed && " · sin suscripción"}
          </div>
          {modulo.roles.map((rol) => {
            const id = `${idPrefijo}-${modulo.moduleCode}-${rol.code}`;
            return (
              <label key={rol.code} htmlFor={id} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                <input
                  id={id}
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={(seleccion[modulo.moduleCode] ?? []).includes(rol.code)}
                  onChange={(e) => onAlternar(modulo.moduleCode, rol.code, e.target.checked)}
                />
                <span style={{ display: "inline", margin: 0, color: "inherit" }}>
                  {rol.name}
                  {rol.isAdmin && <span className="muted"> · administra la organización</span>}
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </fieldset>
  );
}

function FormularioInvitacion({
  asignables,
  onInvitar,
}: {
  asignables: readonly AssignableModuleRoles[];
  onInvitar: (input: { email: string; fullName?: string; roles: Record<string, string[]> }) => Promise<void>;
}): ReactElement {
  const [abierto, setAbierto] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roles, setRoles] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const rolesElegidos = limpiarRoles(roles);

  async function enviar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await onInvitar({ email, roles: rolesElegidos, ...(fullName.length > 0 ? { fullName } : {}) });
      setAbierto(false);
      setEmail("");
      setFullName("");
      setRoles({});
    } catch (err) {
      setError(mensajeDe(err, "No se pudo invitar a esta persona."));
    } finally {
      setEnviando(false);
    }
  }

  if (!abierto) {
    return (
      <button type="button" className="primary" onClick={() => setAbierto(true)}>
        Invitar a alguien
      </button>
    );
  }

  return (
    <form onSubmit={(event) => void enviar(event)} noValidate>
      <fieldset>
        <legend>Invitar a alguien</legend>
        <label htmlFor="invitar-email">
          <span>Correo electrónico</span>
          <input
            id="invitar-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label htmlFor="invitar-nombre">
          <span>Nombre completo (opcional; solo se usa si todavía no tiene cuenta)</span>
          <input id="invitar-nombre" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>

        <RolesPorModulo
          asignables={asignables}
          seleccion={roles}
          idPrefijo="invitar"
          onAlternar={(m, r, marcado) => setRoles((prev) => alternarRol(prev, m, r, marcado))}
        />

        {/* El núcleo envía el correo de primer acceso solo a quien todavía no tiene cuenta
            (docs/FASE-1.md §4). Decirlo aquí evita la pregunta de por qué a unos les llega y a
            otros no. */}
        <p className="muted">
          Quien no tenga cuenta en Continnua recibirá un correo para crear su contraseña. A quien ya
          la tenga solo se le añade a esta organización, sin correo.
        </p>

        {error !== null && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" className="primary" disabled={enviando || Object.keys(rolesElegidos).length === 0}>
            {enviando ? "Invitando…" : "Invitar"}
          </button>
          <button type="button" onClick={() => setAbierto(false)} disabled={enviando}>
            Cancelar
          </button>
        </div>
        {Object.keys(rolesElegidos).length === 0 && (
          <p className="muted">Marca al menos un rol: sin ninguno, la persona entraría sin poder hacer nada.</p>
        )}
      </fieldset>
    </form>
  );
}

function FormularioEdicion({
  miembro,
  asignables,
  esYo,
  onGuardar,
  onCerrar,
}: {
  miembro: Membership;
  asignables: readonly AssignableModuleRoles[];
  esYo: boolean;
  onGuardar: (input: { roles: Record<string, string[]>; status: "active" | "suspended" }) => Promise<void>;
  onCerrar: () => void;
}): ReactElement {
  const [roles, setRoles] = useState<Record<string, string[]>>(miembro.roles);
  const [suspendido, setSuspendido] = useState(miembro.status === "suspended");
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  async function enviar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({ roles: limpiarRoles(roles), status: suspendido ? "suspended" : "active" });
    } catch (err) {
      setError(mensajeDe(err, "No se pudieron guardar los cambios."));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={(event) => void enviar(event)} noValidate>
      <fieldset>
        <legend>{miembro.user.fullName}</legend>
        <p className="muted">{miembro.user.email}</p>

        <RolesPorModulo
          asignables={asignables}
          seleccion={roles}
          idPrefijo={`editar-${miembro.userId}`}
          onAlternar={(m, r, marcado) => setRoles((prev) => alternarRol(prev, m, r, marcado))}
        />

        <label htmlFor={`suspender-${miembro.userId}`} style={{ display: "flex", gap: 8 }}>
          <input
            id={`suspender-${miembro.userId}`}
            type="checkbox"
            style={{ width: "auto" }}
            checked={suspendido}
            onChange={(e) => setSuspendido(e.target.checked)}
          />
          <span style={{ display: "inline", margin: 0, color: "inherit" }}>
            Suspender el acceso a esta organización
          </span>
        </label>

        {esYo && (
          <p className="aviso">
            Estás editando tu propia cuenta. Si te quitas el rol de administrador o te suspendes,
            perderás esta pantalla.
          </p>
        )}

        {error !== null && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" className="primary" disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
          <button type="button" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </button>
        </div>
      </fieldset>
    </form>
  );
}

export function Usuarios({
  orgs,
  error: errorOrgs,
  onCambio,
}: {
  orgs: MeOrgsEntry[] | null;
  error: string | null;
  /**
   * Recarga `GET /me/orgs`. Se llama tras cada cambio porque el propio cambio puede alterar quién
   * administra qué: quien se quita el rol de administrador debe ver desaparecer esta pestaña, no
   * quedarse en una pantalla que ya no le responde.
   */
  onCambio: () => void;
}): ReactElement {
  const authorizedFetch = useAuthorizedFetch();
  const { claims, orgId: orgActiva, switchOrg } = useAuth();

  const gestionables = useMemo(() => (orgs ?? []).filter((entrada) => entrada.canManageMembers), [orgs]);
  const [seleccionada, setSeleccionada] = useState<string | null>(null);
  const [miembros, setMiembros] = useState<Membership[] | null>(null);
  const [asignables, setAsignables] = useState<AssignableModuleRoles[]>([]);
  const [editando, setEditando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [reenviando, setReenviando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // Al entrar, la organización activa de la sesión si se puede administrar; si no, la primera que
  // sí. Elegir cualquier otra cambia la sesión antes de cargar (ver la cabecera del archivo).
  useEffect(() => {
    if (seleccionada !== null || gestionables.length === 0) return;
    const activa = gestionables.find((entrada) => entrada.organization.id === orgActiva);
    setSeleccionada((activa ?? gestionables[0]!).organization.id);
  }, [gestionables, orgActiva, seleccionada]);

  // Sincronizar la sesión con la organización elegida. Separado de la carga a propósito: si se
  // hicieran en el mismo efecto, el cambio de `orgActiva` que provoca `switchOrg` lo dispararía
  // otra vez y todo se pediría dos veces.
  useEffect(() => {
    if (seleccionada === null || seleccionada === orgActiva) return;
    let vivo = true;
    setMiembros(null);
    switchOrg(seleccionada).catch((err: unknown) => {
      if (vivo) setError(mensajeDe(err, "No se pudo cambiar de organización."));
    });
    return () => {
      vivo = false;
    };
  }, [seleccionada, orgActiva, switchOrg]);

  useEffect(() => {
    if (seleccionada === null || seleccionada !== orgActiva) return;
    let vivo = true;
    setError(null);
    Promise.all([api.members(authorizedFetch, seleccionada), api.assignableRoles(authorizedFetch, seleccionada)])
      .then(([lista, roles]) => {
        if (!vivo) return;
        setMiembros([...lista].sort((a, b) => a.user.fullName.localeCompare(b.user.fullName, "es")));
        setAsignables(roles.items);
      })
      .catch((err: unknown) => {
        if (vivo) setError(mensajeDe(err, "No se pudo cargar el equipo de esta organización."));
      });
    return () => {
      vivo = false;
    };
  }, [seleccionada, orgActiva, authorizedFetch, version]);

  if (errorOrgs !== null) return <p className="error">{errorOrgs}</p>;
  if (orgs === null) return <p className="muted">Cargando tus organizaciones…</p>;

  if (gestionables.length === 0) {
    return (
      <div className="aviso">
        No administras ninguna organización. Quien la administre puede darte ese rol, o puedes
        escribir a soporte de Continnua.
      </div>
    );
  }

  const organizacion = gestionables.find((entrada) => entrada.organization.id === seleccionada);
  const miembroEditado = miembros?.find((m) => m.userId === editando);

  async function invitar(input: { email: string; fullName?: string; roles: Record<string, string[]> }): Promise<void> {
    if (seleccionada === null) return;
    await api.inviteMember(authorizedFetch, seleccionada, input);
    setVersion((n) => n + 1);
    onCambio();
  }

  async function guardar(
    userId: string,
    input: { roles: Record<string, string[]>; status: "active" | "suspended" },
  ): Promise<void> {
    if (seleccionada === null) return;
    await api.updateMember(authorizedFetch, seleccionada, userId, input);
    setEditando(null);
    setVersion((n) => n + 1);
    onCambio();
  }

  async function reenviarInvitacion(miembro: Membership): Promise<void> {
    if (seleccionada === null) return;
    setReenviando(miembro.userId);
    setAviso(null);
    setError(null);
    try {
      await api.resendInvitation(authorizedFetch, seleccionada, miembro.userId);
      setAviso(`Se reenvió el enlace de acceso a ${miembro.user.email}.`);
    } catch (err) {
      setError(mensajeDe(err, "No se pudo reenviar la invitación."));
    } finally {
      setReenviando(null);
    }
  }

  async function quitar(miembro: Membership): Promise<void> {
    if (seleccionada === null) return;
    const confirmado = globalThis.confirm(
      `¿Quitar a ${miembro.user.fullName} de ${organizacion?.organization.displayName ?? "la organización"}? ` +
        "Perderá el acceso a todos sus módulos. Su cuenta de Continnua no se borra.",
    );
    if (!confirmado) return;
    try {
      await api.removeMember(authorizedFetch, seleccionada, miembro.userId);
      setVersion((n) => n + 1);
      onCambio();
    } catch (err) {
      setError(mensajeDe(err, "No se pudo quitar a esta persona."));
    }
  }

  return (
    <section className="org-card">
      <header>
        <h2>Usuarios de {organizacion?.organization.displayName ?? "tu organización"}</h2>
      </header>

      {gestionables.length > 1 && (
        <label htmlFor="org-gestionada">
          <span>Organización</span>
          <select
            id="org-gestionada"
            value={seleccionada ?? ""}
            onChange={(e) => {
              setEditando(null);
              setSeleccionada(e.target.value);
            }}
          >
            {gestionables.map((entrada) => (
              <option key={entrada.organization.id} value={entrada.organization.id}>
                {entrada.organization.displayName}
              </option>
            ))}
          </select>
        </label>
      )}

      {error !== null && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {aviso !== null && <p className="aviso">{aviso}</p>}

      {miembros === null ? (
        <p className="muted">Cargando el equipo…</p>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Persona</th>
                <th>Estado</th>
                <th>Roles</th>
                <th aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {miembros.map((miembro) => {
                const esYo = miembro.userId === claims?.sub;
                return (
                  <tr key={miembro.userId}>
                    <td>
                      {miembro.user.fullName}
                      {esYo && <span className="muted"> · tú</span>}
                      <span className="module-role">{miembro.user.email}</span>
                    </td>
                    <td>{ETIQUETA_ESTADO[miembro.status] ?? miembro.status}</td>
                    <td>
                      {Object.entries(miembro.roles).length === 0 ? (
                        <span className="muted">Sin roles</span>
                      ) : (
                        Object.entries(miembro.roles).map(([moduleCode, codigos]) => (
                          <span key={moduleCode} className="module-role">
                            {moduleCode}: {codigos.join(", ")}
                          </span>
                        ))
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button type="button" onClick={() => setEditando(miembro.userId)}>
                        Editar
                      </button>{" "}
                      {miembro.status === "invited" && (
                        <>
                          <button
                            type="button"
                            disabled={reenviando === miembro.userId}
                            onClick={() => void reenviarInvitacion(miembro)}
                          >
                            {reenviando === miembro.userId ? "Reenviando…" : "Reenviar invitación"}
                          </button>{" "}
                        </>
                      )}
                      <button type="button" className="danger" onClick={() => void quitar(miembro)}>
                        Quitar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {miembroEditado ? (
            <FormularioEdicion
              key={miembroEditado.userId}
              miembro={miembroEditado}
              asignables={asignables}
              esYo={miembroEditado.userId === claims?.sub}
              onGuardar={(input) => guardar(miembroEditado.userId, input)}
              onCerrar={() => setEditando(null)}
            />
          ) : (
            <FormularioInvitacion asignables={asignables} onInvitar={invitar} />
          )}
        </>
      )}
    </section>
  );
}
